import {
  Address,
  GoogleAutoCompleteMixedResponse,
  parsePlace,
} from 'civic-champs-shared/formik/components/AddressField'
import _ from 'lodash'
import { importPlaces, isGoogleMapsAvailable } from 'civic-champs-shared/core/google-maps/loader'

type PlaceResult = google.maps.places.PlaceResult
type AutocompleteService = google.maps.places.AutocompleteService
type PlacesService = google.maps.places.PlacesService

let autocompleteService: AutocompleteService
let placesService: PlacesService // Still needed for getAddressFromSuggestion (getDetails call)
let placesServiceDiv: HTMLDivElement

// Create a single div element for PlacesService to reuse
const getPlacesServiceDiv = (): HTMLDivElement => {
  if (!placesServiceDiv) {
    placesServiceDiv = document.createElement('div')
    // Hide the div since it's only used internally by Google Maps API
    placesServiceDiv.style.display = 'none'
  }
  return placesServiceDiv
}

export const getAddressSuggestions = async (input: string): Promise<GoogleAutoCompleteMixedResponse[]> => {
  // Optimization: Don't make API calls for very short inputs
  if (input.length < 3) {
    return Promise.resolve([])
  }

  if (!autocompleteService) {
    await importPlaces()

    if (isGoogleMapsAvailable()) {
      autocompleteService = autocompleteService || new window.google.maps.places.AutocompleteService()
      // Removed placesService initialization - no longer needed after removing findPlaceFromQuery
    }
  }

  return new Promise(resolve => {
    if (!autocompleteService) {
      resolve([])
      return
    }

    autocompleteService.getPlacePredictions(
      {
        input,
        types: ['address'],
        componentRestrictions: { country: ['us', 'ca'] },
      },
      (result: google.maps.places.AutocompletePrediction[]) => {
        resolve(result || [])
      },
    )
  })
}

export const getAddressFromSuggestion = async (suggestion: GoogleAutoCompleteMixedResponse): Promise<Address> => {
  if (!placesService) {
    await importPlaces()

    if (isGoogleMapsAvailable()) {
      placesService = new window.google.maps.places.PlacesService(getPlacesServiceDiv())
    }
  }

  return new Promise((resolve, reject) => {
    if (_.isEmpty(suggestion) || !suggestion.place_id) return reject('No place id')
    if (!placesService) return reject('Places service is not available')

    placesService.getDetails({ placeId: suggestion.place_id }, (result: PlaceResult) => {
      const { state, zip, city, geofencing, country, countryCode, county } = parsePlace(result)
      // After optimization: suggestion is always AutocompletePrediction (has description)
      const description = suggestion.description
      resolve({
        line1: description,
        line2: '',
        utcOffset: 0,
        city,
        zip,
        state,
        geofencing,
        country,
        county,
        countryCode,
      })
    })
  })
}
